import moment from "moment";
import { defineStore } from "pinia";

import {
  AI_CHAT_PSEUDO_SECTION_KEY,
  ARCHIVE_PSEUDO_SECTION_KEY_SUFFIX,
  FILTERS_PSEUDO_SECTION_KEY,
  FOOTER_PSEUDO_SECTION_KEY,
  LEFTBAR_PSEUDO_SECTION_KEY,
} from "~/constants/page";
import { DEFAULT_BLOCK_COLOR } from "~/constants/style";
import { ORDERED_ROLES } from "~/constants/user";
import { USER_KIND_USER } from "~/constants/userKinds";
import {
  AiModel,
  AiThinkingLevel,
  AiVoice,
  EmojiSkinTone,
  EventKind,
  FolderSort,
  TaskDetailMode,
  Theme,
  type TutorialName,
  UserRole,
  UserStatus,
  ViewMode,
} from "~/shared/enums";
import type { Doc, NotificationSettings, Recents, SearchFilter, Task, User } from "~/shared/types";
import { isSetRecentColor } from "~/utils/color";
import { printOrCaptureException } from "~/utils/exceptionClient";
import { lsSet, USER_DUID_LOCAL_STORAGE_KEY } from "~/utils/localStorageManager";

// spell-checker:disable
const LIMIT_AI_CHAT_TENANT_DUIDS = new Set([
  "4tlkNYP3trob", // NBK
]);
const LIMIT_MEMBER_PAGE_EDIT_TENANT_DUIDS = new Set<string>([]);
const LIMIT_IMPORT_EXPORT_TENANT_DUIDS = new Set([
  "4tlkNYP3trob", // NBK
]);
// spell-checker:enable

type State = User & {
  firstDayOfWeek: number;
  lastUrlPath: string | null;
  notificationSettings: NotificationSettings;
  openInNativeApp: boolean;
  hasPassword: boolean;
  recents: Recents;
  mobileAiChatPanelOpenOverride: boolean;
  sessionRoleOverride: UserRole | null;
};

const ROLE_TO_INDEX = Object.fromEntries(ORDERED_ROLES.map((e, i) => [e, i]));

const useUserStore = defineStore("UserStore", {
  // TODO initializing all of these is a type hack that should be fixed
  state: (): State =>
    ({
      duid: "",
      email: "",
      status: UserStatus.DEACTIVATED,
      role: UserRole.GUEST,
      kind: USER_KIND_USER,
      name: "",
      theme: Theme.SYSTEM_DEFAULT,
      imageUrl: null,
      colorHex: DEFAULT_BLOCK_COLOR,
      timezone: "",
      taskDetailMode: TaskDetailMode.RIGHTBAR,
      viewMode: ViewMode.PROJECTS,
      calendarDuid: "",
      aiLanguage: null,
      aiModel: AiModel.AUTO,
      aiThinkingLevel: AiThinkingLevel.LOW,
      aiGuidelines: null,
      aiVoice: AiVoice.MARIN,
      aiVoiceAutoplay: false,
      manualDescription: null,
      generatedDescription: null,
      usingGeneratedDescription: true,
      sections: { expanded: [], collapsed: [] },
      layout: {
        rightbarWidthPx: 650,
        leftbarWidthPx: 225,
        fullscreenRightbarWidthPx: 600,
        chatPanelWidthPx: 400,
        docSort: FolderSort.ORDER,
      },
      recents: {
        recentTaskStack: [],
        recentDocStack: [],
        recentSearchStack: [],
        recentIconStack: [],
        recentEmojiStack: [],
        recentColorCombinedStack: [],
        skinTone: EmojiSkinTone.NONE,
        createTaskCreateMore: false,
        lastBannerClearedAt: null,
        lastCodingLanguage: null,
      },
      tutorialStatuses: {},
      authToken: null,
      googleData: null,
      isAdmin: false,
      firstDayOfWeek: 1,
      lastUrlPath: null,
      notificationSettings: {
        default: false,
        inApp: false,
        email: false,
        slack: false,
        subscriptions: new Map(),
        mutedSpaceDuids: [],
      },
      openInNativeApp: false,
      hasPassword: false,
      mobileAiChatPanelOpenOverride: false,
      sessionRoleOverride: null,
      agent: null,
    }) satisfies State,
  getters: {
    firstName: (state) => {
      const name = state.name.trim();
      return (name.length > 0 ? name.split(/\s+/)[0] : state.email.split("@")[0]).trim();
    },
    localSetupAgentName() {
      return `${this.firstName}'s local agent`;
    },
    getTutorialStatus: (state) => (name: TutorialName) => state.tutorialStatuses[name],
    roleNorm: (state) => state.sessionRoleOverride ?? state.role,
    isRoleGte() {
      return (role: UserRole) => ROLE_TO_INDEX[this.roleNorm as UserRole] >= ROLE_TO_INDEX[role];
    },
    hasAccessToAiChat() {
      return () => this.isRoleGte(UserRole.MEMBER) && !LIMIT_AI_CHAT_TENANT_DUIDS.has(this.$useTenantStore().duid);
    },
    hasAccessToPageEditing() {
      return () =>
        this.isRoleGte(UserRole.ADMIN) ||
        (this.isRoleGte(UserRole.MEMBER) && !LIMIT_MEMBER_PAGE_EDIT_TENANT_DUIDS.has(this.$useTenantStore().duid));
    },
    hasAccessToImportExport() {
      return () => !LIMIT_IMPORT_EXPORT_TENANT_DUIDS.has(this.$useTenantStore().duid);
    },
    rightbarWidthPx: (state) => state.layout.rightbarWidthPx ?? 650,
    leftbarWidthPx: (state) => state.layout.leftbarWidthPx ?? 225,
    chatPanelWidthPx: (state) => state.layout.chatPanelWidthPx ?? 352,
    fullscreenRightbarWidthPx: (state) => state.layout.fullscreenRightbarWidthPx ?? 600,
    docSort: (state) => state.layout.docSort ?? FolderSort.ORDER,
    // Recent stacks
    recentTaskStack: (state) => state.recents.recentTaskStack,
    recentDocStack: (state) => state.recents.recentDocStack,
    recentSearchStack: (state) => state.recents.recentSearchStack,
    recentIconStack: (state) => state.recents.recentIconStack,
    recentEmojiStack: (state) => state.recents.recentEmojiStack,
    recentColorCombinedStack: (state) => state.recents.recentColorCombinedStack.filter(isSetRecentColor),
    skinTone: (state) => state.recents.skinTone ?? EmojiSkinTone.NONE,
    createTaskCreateMore: (state) => state.recents.createTaskCreateMore,
    isSpaceMuted: (state) => (spaceDuid: string) => state.notificationSettings.mutedSpaceDuids.includes(spaceDuid),
  },
  actions: {
    $addToStack<T>(el: T, stack: T[], max: number) {
      const index = stack.indexOf(el);
      if (index !== -1) {
        stack.splice(index, 1);
      }
      stack.unshift(el);
      return stack.slice(0, max);
    },
    async setUser(user: User) {
      if (user.duid !== this.duid) {
        lsSet(USER_DUID_LOCAL_STORAGE_KEY, user.duid);
      }
      // TODO try this instead here and in tenantStore
      // Object.assign(this, user);

      this.duid = user.duid;
      this.email = user.email;
      this.status = user.status;
      this.role = user.role;
      this.kind = user.kind;
      this.name = user.name;
      this.theme = user.theme;
      this.imageUrl = user.imageUrl;
      this.colorHex = user.colorHex;
      const guessedTimezone = moment.tz.guess();
      if (!user.timezone) {
        this.timezone = guessedTimezone;
        this.$backendOld.profile.edit("timezone", this.timezone);
      } else {
        this.timezone = user.timezone;
        if (this.timezone !== guessedTimezone) {
          // TODO times show notification to see if the user wants to change to the guessed timezone
        }
      }
      this.taskDetailMode = user.taskDetailMode;
      this.viewMode =
        user.viewMode === ViewMode.AGENTS && !this.$usePageStore().showAlpha ? ViewMode.PROJECTS : user.viewMode;
      this.calendarDuid = user.calendarDuid;
      this.aiLanguage = user.aiLanguage;
      this.aiModel = user.aiModel;
      this.aiThinkingLevel = user.aiThinkingLevel;
      this.aiGuidelines = user.aiGuidelines;
      this.aiVoice = user.aiVoice;
      this.aiVoiceAutoplay = user.aiVoiceAutoplay;
      this.manualDescription = user.manualDescription;
      this.generatedDescription = user.generatedDescription;
      this.usingGeneratedDescription = user.usingGeneratedDescription;
      if (!this.sections) {
        this.sections = user.sections;
      } else {
        Object.assign(this.sections, user.sections);
      }
      if (!this.layout) {
        this.layout = user.layout;
      } else {
        Object.assign(this.layout, user.layout);
      }
      if (this.layout.docSort === null || this.layout.docSort === undefined) {
        this.layout.docSort = FolderSort.ORDER;
      }
      if (!this.recents) {
        this.recents = user.recents as Recents;
      } else {
        Object.assign(this.recents, user.recents);
      }
      // Only update tutorial statuses if backend has newer values to avoid race conditions
      Object.entries(user.tutorialStatuses).forEach(([name, status]) => {
        const currentStatus = this.tutorialStatuses[name];
        if (currentStatus === undefined || status > currentStatus) {
          this.tutorialStatuses[name] = status;
        }
      });
      this.authToken = user.authToken;
      if (!this.googleData) {
        this.googleData = user.googleData;
      } else {
        Object.assign(this.googleData, user.googleData);
      }
      this.isAdmin = user.isAdmin;
      this.agent = user.agent;

      if (!this.$usePageStore().isPublic) {
        this.$backend.event.create(EventKind.AUTHENTICATE);
      }
    },
    setTheme(theme: Theme) {
      this.theme = theme;
      this.$usePageStore().updateTheme();
      this.$backendOld.profile.edit("theme", theme);
    },
    updateTutorialStatuses(
      updates: { name: TutorialName; status: number }[],
      allowRegressions?: boolean,
      noBackend?: boolean
    ) {
      if (this.$usePageStore().isPublic) {
        return;
      }
      const allowedUpdates = allowRegressions
        ? updates
        : updates.filter(({ name, status }) => status > (this.getTutorialStatus(name) ?? 0));
      if (allowedUpdates.length === 0) {
        return;
      }
      allowedUpdates.forEach(({ name, status }) => {
        this.tutorialStatuses[name] = status;
      });
      if (!noBackend) {
        this.$backendOld.profile.updateTutorialStatuses(allowedUpdates);
      }
    },
    setFirstDayOfWeek(firstDayOfWeek: number) {
      this.firstDayOfWeek = firstDayOfWeek;
    },
    setLastUrlPath(lastUrlPath: string | null) {
      this.lastUrlPath = lastUrlPath;
    },
    setNotificationSettings(notificationSettings: NotificationSettings) {
      this.notificationSettings = {
        ...notificationSettings,
        subscriptions: new Map(
          Object.entries(notificationSettings.subscriptions).map(([k, v]) => [parseInt(k, 10), v])
        ),
        mutedSpaceDuids: notificationSettings.mutedSpaceDuids ?? [],
      };
    },
    setSpaceMuted(spaceDuid: string, muted: boolean) {
      const list = this.notificationSettings.mutedSpaceDuids;
      this.notificationSettings.mutedSpaceDuids = muted
        ? [...new Set([...list, spaceDuid])]
        : list.filter((d) => d !== spaceDuid);
      this.$backendOld.notifications.spaceMute(spaceDuid, muted);
    },
    setOpenInNativeApp(openInNativeApp: boolean) {
      this.openInNativeApp = openInNativeApp;
    },
    setHasPassword(hasPassword: boolean) {
      this.hasPassword = hasPassword;
    },
    getSectionExpanded(key: string, defaultExpanded?: boolean) {
      if (this.sections.expanded.includes(key)) {
        return true;
      }
      if (this.sections.collapsed.includes(key)) {
        return false;
      }
      if (defaultExpanded !== undefined) {
        return defaultExpanded;
      }
      return key !== FILTERS_PSEUDO_SECTION_KEY && !key.endsWith(ARCHIVE_PSEUDO_SECTION_KEY_SUFFIX);
    },
    setSectionExpanded(key: string, value: boolean) {
      if (value) {
        this.sections.expanded = [...this.sections.expanded.filter((e) => e !== key), key];
        this.sections.collapsed = this.sections.collapsed.filter((e) => e !== key);
      } else {
        this.sections.expanded = this.sections.expanded.filter((e) => e !== key);
        this.sections.collapsed = [...this.sections.collapsed.filter((e) => e !== key), key];
      }
      if (this.$usePageStore().isPublicView) {
        return;
      }
      this.$backend.user.update({ duid: this.duid, sections: this.sections });
    },
    setRightbarWidthPx(rightbarWidthPx: number) {
      this.layout.rightbarWidthPx = rightbarWidthPx;
      if (this.$usePageStore().isPublicView) {
        return;
      }
      this.$backend.user.update({ duid: this.duid, layout: this.layout });
    },
    setLeftbarWidthPx(leftbarWidthPx: number) {
      this.layout.leftbarWidthPx = leftbarWidthPx;
      if (this.$usePageStore().isPublicView) {
        return;
      }
      this.$backend.user.update({ duid: this.duid, layout: this.layout });
    },
    setLeftbarExpanded(expanded: boolean) {
      this.setSectionExpanded(LEFTBAR_PSEUDO_SECTION_KEY, expanded);
    },
    getLeftbarExpanded(): boolean {
      return this.getSectionExpanded(LEFTBAR_PSEUDO_SECTION_KEY);
    },
    setFullscreenRightbarWidthPx(fullscreenRightbarWidthPx: number) {
      this.layout.fullscreenRightbarWidthPx = fullscreenRightbarWidthPx;
      if (this.$usePageStore().isPublicView) {
        return;
      }
      this.$backend.user.update({ duid: this.duid, layout: this.layout });
    },
    setChatPanelWidthPx() {
      if (this.$usePageStore().isPublicView) {
        return;
      }
      this.$backend.user.update({ duid: this.duid, layout: this.layout });
    },
    async updateRecents(update: Partial<Recents>, noBackend?: boolean) {
      if (this.$usePageStore().isPublic) {
        return;
      }
      this.recents = {
        ...this.recents,
        ...update,
      };
      if (!noBackend) {
        await this.$backend.user.update({ duid: this.duid, recents: this.recents });
      }
    },
    // Recent stacks
    addToRecentIconStack(icon: string) {
      const updatedStack = this.$addToStack(icon, this.recentIconStack, 24);
      return this.updateRecents({ recentIconStack: updatedStack });
    },
    addToRecentEmojiStack(emoji: string) {
      const updatedStack = this.$addToStack(emoji, this.recentEmojiStack, 24);
      return this.updateRecents({ recentEmojiStack: updatedStack });
    },
    addToRecentColorStack(color: string, isBackground: boolean) {
      if (!isSetRecentColor({ color })) {
        return;
      }
      const currentStack = [...this.recentColorCombinedStack];
      const existingIndex = currentStack.findIndex(
        (entry) => entry.color === color && entry.isBackground === isBackground
      );
      if (existingIndex !== -1) {
        currentStack.splice(existingIndex, 1);
      }
      currentStack.unshift({ color, isBackground });
      const updatedStack = currentStack.slice(0, 6);
      this.updateRecents({ recentColorCombinedStack: updatedStack });
    },
    setSkinTone(skinTone: EmojiSkinTone) {
      return this.updateRecents({ skinTone });
    },
    addRecentSearch(search: SearchFilter) {
      const updatedStack = this.$addToStack(search, this.recentSearchStack, 100);
      return this.updateRecents({ recentSearchStack: updatedStack });
    },
    removeRecentSearch(duid: string) {
      const currentStack = [...this.recentSearchStack];
      const index = currentStack.findIndex((entry) => entry.duid === duid);
      if (index === -1) {
        return;
      }
      currentStack.splice(index, 1);
      this.updateRecents({ recentSearchStack: currentStack });
    },
    addRecentTask(task: Task, noBackend: boolean = true) {
      if (task.drafterDuid) {
        return undefined;
      }
      const updatedStack = this.$addToStack(task.duid, this.recentTaskStack, 100);
      return this.updateRecents({ recentTaskStack: updatedStack }, noBackend);
    },
    removeRecentTask(duid: string, noBackend: boolean = true) {
      const currentStack = [...this.recentTaskStack];
      const index = currentStack.findIndex((entry) => entry === duid);
      if (index === -1) {
        return;
      }
      currentStack.splice(index, 1);
      this.updateRecents({ recentTaskStack: currentStack }, noBackend);
    },
    addRecentDoc(doc: Doc, noBackend: boolean = true) {
      const updatedStack = this.$addToStack(doc.duid, this.recentDocStack, 100);
      return this.updateRecents({ recentDocStack: updatedStack }, noBackend);
    },
    removeRecentDoc(duid: string, noBackend: boolean = true) {
      const currentStack = [...this.recentDocStack];
      const index = currentStack.findIndex((entry) => entry === duid);
      if (index === -1) {
        return;
      }
      currentStack.splice(index, 1);
      this.updateRecents({ recentDocStack: currentStack }, noBackend);
    },
    setCreateTaskCreateMore(createTaskCreateMore: boolean) {
      return this.updateRecents({ createTaskCreateMore });
    },
    setDocSort(docSort: FolderSort) {
      this.layout.docSort = docSort;
      this.$backend.user.update({ duid: this.duid, layout: this.layout });
    },
    getShowFilters(): boolean {
      return this.getSectionExpanded(FILTERS_PSEUDO_SECTION_KEY);
    },
    setShowFilters(newShowFilters: boolean) {
      this.setSectionExpanded(FILTERS_PSEUDO_SECTION_KEY, newShowFilters);
    },
    getShowFooter(): boolean {
      return this.getSectionExpanded(FOOTER_PSEUDO_SECTION_KEY);
    },
    setShowFooter(newShowFooter: boolean) {
      this.setSectionExpanded(FOOTER_PSEUDO_SECTION_KEY, newShowFooter);
    },
    getAiChatPanelOpen(): boolean {
      const { isMobile } = this.$usePageStore();
      if (isMobile) {
        return this.mobileAiChatPanelOpenOverride;
      }
      return this.getSectionExpanded(AI_CHAT_PSEUDO_SECTION_KEY);
    },
    setAiChatPanelOpen(open: boolean) {
      const { isMobile } = this.$usePageStore();
      if (isMobile) {
        this.mobileAiChatPanelOpenOverride = open;
        return;
      }
      this.setSectionExpanded(AI_CHAT_PSEUDO_SECTION_KEY, open);
    },
    setTaskDetailMode(taskDetailMode: TaskDetailMode) {
      this.taskDetailMode = taskDetailMode;
      this.$backendOld.profile.edit("taskDetailMode", taskDetailMode);
    },
    setViewMode(viewMode: ViewMode) {
      this.viewMode = viewMode;
      this.$backendOld.profile.edit("viewMode", viewMode);
    },
    setSessionRoleOverride(role: UserRole | null) {
      this.sessionRoleOverride = role;
    },
    forceLogout() {
      this.$router.push({ name: "logout" });
      try {
        throw new Error("Forced logout");
      } catch (error) {
        printOrCaptureException(error);
      }
    },
  },
});

export default useUserStore;
